import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import { BrandLogo } from '../shared/BrandLogo'
import './web-test-motion.css'
import './web-test-hero.css'
import './web-test-legal.css'
import { WebsiteThemeToggle } from './WebsiteThemeToggle'
import { portalLinks } from './portal-links'
import { SeoHead } from './seo/SeoHead'
import { defaultPageGraph } from './seo/jsonld'
import { getPageSeo } from './seo/pages'
import { HERO_RISK_NOTICE } from './risk-copy'
import { trackClientLogin, trackContact, trackOpenAccount, trackSupport } from '../analytics/track'

/** Verified in Client Portal SupportPage source — public contact may reference this email. */
const PUBLIC_SUPPORT_EMAIL = 'support@perfectpredict.com'

type SimpleSection = { heading: string; body: string; bullets?: string[] }

type SimpleDoc = {
  path: '/about' | '/support' | '/contact'
  eyebrow: string
  title: string
  intro?: string
  paragraphs?: string[]
  sections?: SimpleSection[]
}

const DOCS: Record<'about' | 'support' | 'contact', SimpleDoc> = {
  about: {
    path: '/about',
    eyebrow: 'Company',
    title: 'About Perfect Predict',
    intro:
      'Perfect Predict is a structured prediction trading platform focused on clear parameters, trader-selected direction, and MetaTrader 5 execution infrastructure.',
    sections: [
      {
        heading: 'What Perfect Predict is',
        body: 'Perfect Predict offers structured prediction trading on institutional MetaTrader 5 infrastructure. The public website explains the product; accounts are managed in the secure Client Portal.',
      },
      {
        heading: 'Structured prediction trading',
        body: 'Instead of an open-ended discretionary ticket on the marketing site, Perfect Predict presents predefined strategy parameters — target, stop and illustrative payout examples — so the structure is visible before you trade.',
      },
      {
        heading: 'You choose BUY or SELL',
        body: 'The trader always selects market direction. Perfect Predict does not choose BUY or SELL for you.',
      },
      {
        heading: 'Predefined strategy parameters',
        body: 'SAFE, BALANCE and AGGRESSIVE are strategy/risk-reward profiles with predefined parameters. They describe structure — they do not guarantee outcomes.',
      },
      {
        heading: 'Gold and Silver',
        body: 'Live prediction markets currently include Gold (XAU/USD) and Silver (XAG/USD).',
      },
      {
        heading: 'MT5 execution infrastructure',
        body: 'Trade execution connects through MetaTrader 5 infrastructure, with day-to-day management in the Perfect Predict Client Portal.',
      },
      {
        heading: 'Secure Client Portal',
        body: 'Registration, Live and Demo accounts, funding, withdrawals and support messaging are handled through the Client Portal — not as live account data embedded on the public website.',
      },
    ],
  },
  support: {
    path: '/support',
    eyebrow: 'Support',
    title: 'Support',
    intro:
      'For account-specific help, use Client Portal Support — assistance tied to your Perfect Predict identity. The categories below describe common help topics.',
    sections: [
      {
        heading: 'Account access',
        body: 'Sign-in issues, password resets and portal access are handled through Client Portal authentication flows and Support.',
      },
      {
        heading: 'Verification',
        body: 'Identity or account verification steps required for Live funding or withdrawals are completed through the Client Portal when requested.',
      },
      {
        heading: 'Funding',
        body: 'Live accounts are funded through the Client Portal using the supported method (Uniwire). Demo accounts require no funding.',
      },
      {
        heading: 'Withdrawals',
        body: 'Withdrawal requests and status are managed in the Client Portal. Processing can depend on verification and provider checks.',
      },
      {
        heading: 'Trading access',
        body: 'Questions about launching trading access, Demo vs Live, or MetaTrader 5 connectivity should go through Client Portal Support.',
      },
      {
        heading: 'Technical support',
        body: 'Browser, portal availability and related technical issues can be reported through Client Portal Support after you sign in.',
      },
    ],
  },
  contact: {
    path: '/contact',
    eyebrow: 'Contact',
    title: 'Contact',
    intro: 'Contact is for general enquiries. For account-specific help, use Client Portal Support after signing in.',
    sections: [
      {
        heading: 'General enquiries',
        body: `You may email ${PUBLIC_SUPPORT_EMAIL} for general questions. Do not send passwords or full payment card details by email.`,
      },
      {
        heading: 'Account-specific help',
        body: 'If you already have an account, sign in and use Client Portal Support so assistance can be tied to your Perfect Predict identity.',
      },
      {
        heading: 'New to Perfect Predict?',
        body: 'Create an account to access secure messaging and account assistance in the Client Portal.',
      },
    ],
  },
}

export function WebTestSimplePage({ doc }: { doc: keyof typeof DOCS }) {
  const content = DOCS[doc]
  const seo = getPageSeo(content.path)!

  return (
    <div className="wt-page wl-page">
      <SeoHead path={content.path} jsonLd={defaultPageGraph(seo)} />
      <header className="wl-header">
        <div className="wl-header__inner">
          <BrandLogo className="wt-logo" tone="light" to="/" />
          <nav className="wl-header__nav" aria-label="Secondary">
            <Link to="/blog">Blog</Link>
            <Link to="/about">About</Link>
            <Link to="/support">Support</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <div className="wl-header__actions">
            <WebsiteThemeToggle />
            <a
              className="wt-btn wt-btn--ghost"
              href={portalLinks.login}
              onClick={() => trackClientLogin('header')}
            >
              Sign In
            </a>
            <a
              className="wt-btn wt-btn--primary"
              href={portalLinks.register}
              onClick={() => trackOpenAccount('header')}
            >
              Open Live Account
            </a>
          </div>
        </div>
      </header>

      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">{content.eyebrow}</p>
          <h1 className="wl-title">{content.title}</h1>
          {content.intro ? (
            <p className="wl-section" style={{ color: 'var(--wt-muted)' }}>
              {content.intro}
            </p>
          ) : null}
          {doc === 'about' ? (
            <p className="wl-section" style={{ color: 'var(--wt-muted)', fontSize: '0.95rem' }}>
              {HERO_RISK_NOTICE}{' '}
              <Link to="/risk-disclosure">Read the Risk Disclosure</Link>.
            </p>
          ) : null}
          {content.sections?.map((section) => (
            <section key={section.heading} className="wl-section">
              <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>
                {section.heading}
              </h2>
              <p style={{ margin: 0, color: 'var(--wt-muted)' }}>{section.body}</p>
              {section.bullets ? (
                <ul style={{ margin: '10px 0 0', paddingLeft: '1.2rem', color: 'var(--wt-muted)' }}>
                  {section.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
          {content.paragraphs?.map((p) => (
            <p key={p.slice(0, 40)} className="wl-section" style={{ color: 'var(--wt-muted)' }}>
              {p}
            </p>
          ))}
          {doc === 'contact' ? (
            <p className="wl-back">
              <a
                className="wt-btn wt-btn--secondary"
                href={`mailto:${PUBLIC_SUPPORT_EMAIL}`}
                onClick={() => trackContact('landing_page')}
              >
                Email {PUBLIC_SUPPORT_EMAIL}
              </a>
            </p>
          ) : null}
          {doc === 'support' || doc === 'contact' ? (
            <p className="wl-back">
              <a
                className="wt-btn wt-btn--primary"
                href={portalLinks.support}
                onClick={() => (doc === 'support' ? trackSupport('landing_page') : trackContact('landing_page'))}
              >
                Open Client Portal Support
              </a>
            </p>
          ) : null}
          <p className="wl-back">
            <Link to="/">← Back to Perfect Predict</Link>
          </p>
        </div>
      </main>
    </div>
  )
}

export function WebsiteAuthRedirect({ kind }: { kind: 'login' | 'register' }) {
  const href = kind === 'login' ? portalLinks.login : portalLinks.register
  const path = kind === 'login' ? '/login' : '/register'
  const seo = getPageSeo(path)!

  useEffect(() => {
    window.location.replace(href)
  }, [href])

  return (
    <div className="wt-page wl-page">
      <SeoHead path={path} jsonLd={defaultPageGraph(seo)} />
      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">Account</p>
          <h1 className="wl-title">{kind === 'login' ? 'Sign In' : 'Create Account'}</h1>
          <p style={{ color: 'var(--wt-muted)' }}>Redirecting to the Client Portal…</p>
          <p className="wl-back">
            <a href={href}>Continue</a>
          </p>
        </div>
      </main>
    </div>
  )
}
